/**
 * תפקידים והרשאות — what a role confers, and granting one. OA-0001 §3.2 / §3.3 / §3.7, D5 / CG-1.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * NO ROLE ON THIS SCREEN GRANTS ACCESS TO ANY COMPANY, AND THE SCREEN SAYS SO.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Owner decision D5: *"Tenant Owner is NOT automatically entitled to every Company. Exclusion,
 * INCLUDING CONFLICT OF INTEREST, must be supported. Access is ALWAYS an explicit grant."* CG-1
 * states the general form — no role, ownership flag or seniority implies a grant — and the reason it
 * has to be said out loud on a roles screen is that every product a bookkeeper has ever used got
 * this wrong: "admin" meant "sees everything", and a firm that audits two sides of one transaction
 * cannot use a product where it does.
 *
 * So the capability list under each role is exactly what that role lets a person DO, and the
 * question "on which companies" is answered on a different screen, from a different table, under a
 * different capability. The sentence at the top is not reassurance; it is the specification.
 *
 * ── WHY THE ASSIGNMENT IS HERE AND NOT ON A SCREEN OF ITS OWN ───────────────────────────────────
 *
 * A third screen was the obvious alternative and it is the wrong one, for two reasons.
 *
 * The first is that a role is not a name — it is the capability list underneath it, and choosing one
 * without reading that list is how an office grants `ניהול הרשאות לחברות` to a temp. A separate
 * assignment screen would have shown a picker of five Hebrew names and left the catalogue on the
 * page the user just left; here the picker IS the catalogue, one control per card, so nobody grants
 * a role they have not just read.
 *
 * The second is that a role screen and an assignment screen would have had to state the same
 * specification twice — D5's "a role grants no company", the two-axis rule, the delegation ceiling —
 * and a rule written down in two places is a rule that drifts in one of them. What stays apart is
 * the thing that must stay apart: ROLE (what a user may do) is here, COMPANY ACCESS (which companies
 * they may do it on) is `TenantCompanyAccessScreen`, two tables, two capabilities, two routes. That
 * separation is between screens; the catalogue and its picker were never the two halves.
 *
 * ── THE TWO AXES ARE SHOWN AS TWO AXES ──────────────────────────────────────────────────────────
 *
 * When a person is chosen, the screen states BOTH of their axes side by side and says that either
 * one alone does nothing. That is the same sentence the invitation screen already carries — *"לא
 * יוכל לעשות דבר עד שיוענק לו תפקיד, ולא יראה אף חברה עד שתוענק לו גישה אליה"* — and it is repeated
 * rather than referenced because this is the screen where somebody is about to conclude that they
 * have finished the job.
 *
 * Both figures are the SERVER'S: the roles from the membership, the company count from the grants
 * table. Nothing here derives one from the other, which is exactly what a product that collapsed the
 * two axes would have to do.
 *
 * ── THE CEILING, AND THE THREE CUTS THAT ARE NOW REPAIRED ───────────────────────────────────────
 *
 * OA-0001 §3.3 bounds §3.2: the customer runs their own authorization system, WITHIN the granter's
 * own capabilities. `checkDelegationCeiling` returns the EXCESS rather than a boolean precisely so
 * the refusal can name capabilities.
 *
 * A previous round recorded that the detail was cut in three places, none of them here:
 *
 *   1. `services/customer-admin.ts` `assignRoles` returned `{ reason }` — the `excess` the domain
 *      module computed was dropped one line after it was produced;
 *   2. the route answered `{ error: reason }` with no list even if it had one;
 *   3. `api/client.ts` kept only a support reference from a conflict body.
 *
 * ALL THREE ARE CLOSED. The service carries `excess`, the route puts it on the 409 body, and
 * `ConflictError` carries it across. So the refusal on this screen is no longer *"the server refused
 * and nothing changed"* — it NAMES THE CAPABILITIES, in the catalogue's own Hebrew, through
 * `roleAssignmentText.ts` and `<RoleRefusalNotice>`, which is the whole reason the domain module
 * returns a list instead of a boolean.
 *
 * `REFUSED_WITHOUT_DETAIL_HE` survives for the case it was written for and no longer for the common
 * one: a refusal whose reason this build has no Hebrew for. It says the two things that stay true
 * regardless — the server refused, and it refused before writing — and it does NOT guess which of the
 * four refusals it was, because guessing would tell an office it hit a ceiling when a colleague had
 * merely been removed from the list.
 *
 * ── ROLES CAN NOW BE DEFINED, NOT ONLY ASSIGNED ────────────────────────────────────────────────
 *
 * OA-0001 §3.2 says the customer OPERATES their own authorization system. That was half true while
 * roles could be assigned and never authored — an office whose seeded five roles did not fit had no
 * move at all. The authoring form is at the bottom of this screen, under the catalogue, deliberately:
 * a role IS its capability list, and the list is the thing being composed.
 *
 * The ceiling applies to authoring exactly as it applies to assignment, and for a sharper reason —
 * a bounded administrator who could write any capability into a role would not need to assign
 * anything, because they could edit a role they already hold. The server checks; this screen states
 * the rule and renders the refusal.
 *
 * ── THE CAPABILITY NAMES ARE THE SERVER'S ───────────────────────────────────────────────────────
 *
 * 04 §7.1 / 02 §5.1 CF-64: **the code declares which capabilities exist; the table stores only their
 * policy**, and startup refuses to boot if the two disagree in either direction. The Hebrew here is
 * `descriptionHe` from that catalogue verbatim, and the mirror test fails on a drift of one
 * character — a second Hebrew name invented in the UI would be a second catalogue.
 *
 * A capability this build has no Hebrew for is shown as an explicit gap, never as its code: 09 §9.1
 * V3 allows exactly one technical string on a customer's screen and it is a support reference.
 *
 * ── AND WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────────────────────────
 *
 * No "select all", no admin switch, no capability checkboxes. OA-0001 §3.7: *"They do not collapse
 * into one admin flag."* A control that granted every capability at once would be that flag wearing
 * a Hebrew label, and `adminScreens.test.ts` counts the controls on this screen so it cannot appear.
 */

import { useCallback, useMemo, useState } from 'react';
import { useLocale } from '../i18n/LocaleProvider';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { FactList } from '../components/FactList';
import { RoleRefusalNotice } from '../components/RoleRefusalNotice';
import { StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { MembershipSummary, RoleAssignmentRefusalReason } from '../api/contract';
import { ROLE_ASSIGNMENT_REFUSALS } from '../api/contract';
import { capabilityDescriptionHe, roleLabel } from '../api/adminVocabulary';
import { REFUSED_WITHOUT_DETAIL_HE } from '../api/roleAssignmentText';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { fixtureRoleAssignmentRefusals, fixturesEnabled } from '../api/fixtures';
import { ServerRead } from '../components/ServerRead';
import { bothOf, listOf, useServerData } from '../api/useServerData';
import { routes } from '../routes';


type Outcome =
  | { readonly kind: 'assigned'; readonly roleCount: number }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

/** Only a reason the mirror test proves the server can produce. Anything else says less. */
function knownRefusal(reason: string): RoleAssignmentRefusalReason | null {
  const known: readonly string[] = ROLE_ASSIGNMENT_REFUSALS;
  return known.includes(reason) ? (reason as RoleAssignmentRefusalReason) : null;
}

export function TenantRolesScreen(): JSX.Element {
  const { t } = useLocale();

  const ctx = useWorkContext();
  const [membershipUid, setMembershipUid] = useState('');
  const [chosenRoleCodes, setChosenRoleCodes] = useState<readonly string[]>([]);
  const [pending, setPending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  /*
   * THREE READS, AND THE ASSIGNMENT FORM DEPENDS ON TWO OF THEM. **Phase 2 §6, vertical A.**
   *
   * `tenant.role.read` and `tenant.member.read` are what the form is BUILT FROM — there is no person
   * to choose and no role to tick without them — so the form lives inside a `<ServerRead>` over the
   * pair. Rendering an empty person-picker while a read was refused would invite an administrator to
   * conclude their office has no staff.
   *
   * `tenant.grant.read` is the OTHER AXIS, read separately on purpose: 01 §A5 keeps roles and company
   * access as two independent grants, and merging them into one response would be the first step
   * towards a screen that treats them as one thing.
   */
  const readRoles = useCallback(() => callOperation('tenant.role.read', {}), []);
  const readMembers = useCallback(() => callOperation('tenant.member.read', {}), []);
  const readGrants = useCallback(() => callOperation('tenant.grant.read', {}), []);
  const rolesRead = useServerData(readRoles);
  const membersRead = useServerData(readMembers);
  const grantsRead = useServerData(readGrants);

  const roles = listOf(rolesRead.result, 'roles');
  const members: readonly MembershipSummary[] = listOf(membersRead.result, 'members');
  const grants = listOf(grantsRead.result, 'grants');
  const refusalSamples = fixturesEnabled() ? fixtureRoleAssignmentRefusals : [];

  const chosenMember = useMemo(
    () => members.find((m) => m.membershipUid === membershipUid) ?? null,
    [members, membershipUid],
  );

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={t('roles.title')} requires="TENANT" actual={ctx.surface} />;
  }

  /** The OTHER axis, as the server holds it. Counted, never derived from the roles above it. */
  const companiesGranted =
    chosenMember === null
      ? 0
      : grants.filter((g) => g.membershipUid === chosenMember.membershipUid).length;

  const roleNameHe = (code: string): string =>
    (() => {
      const role = roles.find((r) => r.roleCode === code);
      return role === undefined ? code : roleLabel(role);
    })();

  /**
   * Choosing a person loads THEIR CURRENT SET, because the route replaces the set rather than adding
   * to it. A form that started empty would turn "give this person one more role" into "take the rest
   * away", silently, on the most sensitive write the office has.
   */
  function chooseMembership(member: MembershipSummary): void {
    setMembershipUid(member.membershipUid);
    setChosenRoleCodes(member.roleCodes);
    setOutcome(null);
  }

  function toggleRoleCode(roleCode: string): void {
    setChosenRoleCodes((current) =>
      current.includes(roleCode)
        ? current.filter((code) => code !== roleCode)
        : [...current, roleCode],
    );
  }

  async function run(action: () => Promise<Outcome>): Promise<void> {
    setPending(true);
    setOutcome(null);
    try {
      setOutcome(await action());
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setPending(false);
    }
  }

  function submit(e: FormEvent): void {
    e.preventDefault();
    void run(async () => {
      // The real route: it takes the membership and the WHOLE set of role codes, and it takes no
      // company and no capability list. A write, so it is never retried — the client cannot know
      // whether the server committed, and a repeated assignment is a repeated audit event on the
      // office's permissions.
      const result = await callOperation('tenant.user.role.assign', {
        body: { membershipUid, roleCodes: chosenRoleCodes },
      });
      return { kind: 'assigned', roleCount: result.roleCount };
    });
  }


  const submitLabelHe =
    chosenRoleCodes.length === 0
      ? t('roles.removeAll')
      : chosenRoleCodes.length === 1
        ? t('roles.grantOne')
        : `${t('roles.grantAction')} · ${String(chosenRoleCodes.length)}`;

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      titleHe={t('roles.title')}
      backToParent={{ labelHe: t('settings.title'), href: routes.tenantAdmin }}
    >
      {/*
        ══ FIVE PARAGRAPHS STOOD HERE, ON EVERY VISIT. **2026-10-28.** ══════════════════════

        Two notices, 700 characters, explaining the permission model to an office administrator
        before they had done anything: that a role is not company access, that both are needed, that
        you cannot grant what you do not hold, that an assignment replaces the set. The Owner:
        *"לא צריך את כל המידע הזה ... זה ממש מיותר. תראה את האייקונט, איזה נקי זה."*

        NOTHING THEY DESCRIBED IS WEAKENED, because none of it was enforced by being written here:

          · a role grants no company access — D5, in the schema: `core.tenant_user_role` holds no
            company, and `core.user_company_access` is a separate table with a separate route;
          · you cannot grant beyond your own set — `checkRoleAssignment`, which REFUSES the write
            and names the excess capabilities in the refusal the person actually sees;
          · an assignment replaces the set — the store's own `REPLACE, not merge`, and the hint
            beside the tick list says it in six words.

        A rule the product enforces does not need a paragraph announcing it; a rule that needed the
        paragraph was never a rule.
      */}

      <div className="actions-row">
        <a className="btn" href={routes.tenantCompanyAccess}>
          {t('access.title')}
        </a>
        <a className="btn" href={routes.tenantUsers}>
          {t('users.title')}
        </a>
        {/*
          AUTHORING IS A DIFFERENT SCREEN, and the separation is not cosmetic. Assigning a role is
          choosing from a catalogue; DEFINING one is composing a capability list, and a control that
          could compose one here would be the capability picker OA-0001 §3.7 keeps off the assignment
          path — an office would grant "everything" by editing rather than by choosing.
        */}
        <a className="btn" href={routes.tenantRoleAuthoring}>
          {t('settings.e.roleAuthoring')}
        </a>
      </div>

      <ServerRead
        result={bothOf(rolesRead.result, membersRead.result)}
        whatHe={t('roles.readingBoth')}
      >
        {() =>
          roles.length === 0 ? (
            <StateBlock
              titleHe={t('roles.emptyTitle')}
              bodyHe={t('roles.emptyBody')}
              tone="empty"
            />
          ) : (
            <form className="form" onSubmit={(e) => void submit(e)}>
              <fieldset className="field field--choice">
                <legend className="field__label">{t('access.user')}</legend>
                {members.length === 0 ? (
                  <span className="field__hint">{t('users.emptyShort')}</span>
                ) : (
                  members.map((m) => (
                    <label key={m.membershipUid} className="choice">
                      <input
                        type="radio"
                        name="membership"
                        value={m.membershipUid}
                        checked={membershipUid === m.membershipUid}
                        onChange={() => chooseMembership(m)}
                      />
                      <span>
                        {m.displayName}
                        <span className="choice__hint">
                          {m.roleCodes.length === 0
                            ? t('roles.noneInvited')
                            : `${t('roles.currentRoles')}: ${m.roleCodes.map(roleNameHe).join(' · ')}`}
                        </span>
                      </span>
                    </label>
                  ))
                )}
              </fieldset>

              {chosenMember === null ? null : (
                <section className="notice" data-tone="info">
                  <h2 className="notice__title">{`${t('roles.twoPermissions')} · ${chosenMember.displayName}`}</h2>
                  <FactList
                    facts={[
                      {
                        labelHe: t('roles.whatTheyMayDo'),
                        value:
                          chosenMember.roleCodes.length === 0
                            ? null
                            : chosenMember.roleCodes.map(roleNameHe).join(' · '),
                        absentHe: t('roles.noneCanDoNothing'),
                      },
                      {
                        labelHe: t('roles.inWhichCompanies'),
                        value: companiesGranted === 0 ? null : `${t('roles.companiesGranted')}: ${String(companiesGranted)}`,
                        absentHe: t('roles.noCompanies'),
                      },
                    ]}
                  />
                  <p className="notice__body">
                    {t('roles.firstColumnOnly')}{' '}<a href={routes.tenantCompanyAccess}>{t('access.title')}</a>
                  </p>
                </section>
              )}

              {/*
                The picker IS the catalogue: one control per role, on the card that lists what the role
                confers. There is no control that selects them all, and none that selects a capability —
                a role is the unit that is granted, and OA-0001 §3.7 is why there is no third option.
              */}
              <fieldset className="field field--choice">
                <legend className="field__label">{t('roles.toGrant')}</legend>
                <div className="roles">
                  {roles.map((role) => {
                    const described = role.capabilities.map((code) => ({
                      code,
                      he: capabilityDescriptionHe(code),
                    }));
                    const unnamed = described.filter((d) => d.he === null).length;
                    return (
                      <article key={role.roleCode} className="role">
                        {/* The control and the name are one target; the capability list below is the
                            reason the target is on the card and not in a list of five Hebrew names. */}
                        <label className="role__choice">
                          <input
                            type="checkbox"
                            name="roleCodes"
                            value={role.roleCode}
                            checked={chosenRoleCodes.includes(role.roleCode)}
                            onChange={() => toggleRoleCode(role.roleCode)}
                          />
                          <span className="role__title">{roleLabel(role)}</span>
                        </label>
                        <p className="role__count">
                          {`${t('roles.grantsCount')}: ${String(role.capabilities.length)} · ${t('roles.noCompanyAccess')}`}
                        </p>
                        {role.isPersonal ? (
                          <p className="role__personal">{t('roles.personalRole')}</p>
                        ) : null}
                        <ul className="role__capabilities">
                          {described
                            .filter((d): d is { code: string; he: string } => d.he !== null)
                            .map((d) => (
                              <li key={d.code} className="role__capability">
                                {d.he}
                              </li>
                            ))}
                        </ul>
                        {unnamed === 0 ? null : (
                          <p className="role__unnamed">
                            {`${t('roles.unnamedSuffix')}: ${String(unnamed)}`}
                          </p>
                        )}
                      </article>
                    );
                  })}
                </div>
                <span className="field__hint">
                  {t('roles.replacesExactly')}
                </span>
              </fieldset>

              <div className="actions-row">
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={pending || membershipUid === ''}
                >
                  {pending ? t('common.sending') : submitLabelHe}
                </button>
              </div>
              {/* No standing sentence here. **Owner, 2026-10-28:** *"אני לא רוצה לראות הסברים בתוך המערכת. המערכת צריכה להיות נקיה."* It said a role grant gives no company access and is recorded in the audit journal. Both are true on every grant; neither is a decision the person makes here. */}
            </form>
          )
        }
      </ServerRead>

      {outcome === null ? null : outcome.kind === 'assigned' ? (
        <StateBlock
          titleHe={t('roles.grantedTitle')}
          bodyHe={`${String(outcome.roleCount)} ${t('roles.assignedNote')}`}
          nextStep={
            <a className="btn btn--nav" href={routes.tenantCompanyAccess}>
              {t('access.grantTitle')}
            </a>
          }
        />
      ) : (
        <ActionFailureNotice
          failure={outcome.failure}
          /*
           * THE FACTOR IS GIVEN HERE. **Owner section 3, 2026-10-27.**
           *
           * Without this handler `ActionFailureNotice` has no way to satisfy an MFA demand in
           * place and falls through to the arm that LINKS TO ANOTHER ADDRESS, which unmounts
           * this screen and loses whatever was half-entered. Every write this screen makes is
           * declared `per_action`, so the demand is a state it really reaches.
           *
           * NOTHING IS RE-SENT. `TenantUsersScreen` states the rule: *"A write that replays
           * itself after an authentication step is a write nobody pressed."* Clearing the
           * refusal puts the person back where they were, and they press again.
           */
          onStepUpSatisfied={() => setOutcome(null)}
          renderConflict={(failure) => {
            /*
              THE CEILING REFUSAL, NAMED — which is what the three repaired cuts bought.
              `excess` is the list `checkDelegationCeiling` returned instead of a boolean, and
              `<RoleRefusalNotice>` prints it in the catalogue's own Hebrew. A refusal that named
              nothing is what this branch replaced.
            */
            const reason = knownRefusal(failure.body.error);
            if (reason === null) {
              // A refusal this build has no Hebrew for. It says the two things that stay true and
              // does NOT guess which of the four it was — guessing would tell an office it hit a
              // ceiling when a colleague had merely been removed from the list.
              return (
                <StateBlock
                  titleHe={REFUSED_WITHOUT_DETAIL_HE.titleHe}
                  bodyHe={REFUSED_WITHOUT_DETAIL_HE.bodyHe}
                  tone="error"
                  nextStep={<p>{REFUSED_WITHOUT_DETAIL_HE.nextStepHe}</p>}
                />
              );
            }
            return (
              <RoleRefusalNotice refusal={{ reason, excess: failure.body.excess ?? [] }} />
            );
          }}
        />
      )}

      {/*
        THE REFUSALS, IN THE WORDS THEY WILL USE — from development data, and labelled as such.

        This is not a claim about the office's current permissions and it is not an outcome of
        anything: it is the wording of the two ceiling refusals, shown so it can be read before there
        is a server that returns them. It exists only while `fixturesEnabled()` is true, so it is not
        in a production build at all — the same rule that governs every other fixture on this screen.
      */}
      {refusalSamples.length === 0 ? null : (
        <section className="notice" data-tone="info">
          <h2 className="notice__title">{t('roles.refusalPreview')}</h2>
          {refusalSamples.map((refusal) => (
            <RoleRefusalNotice key={refusal.reason} refusal={refusal} announce={false} />
          ))}
        </section>
      )}
    </AppShell>
  );
}
